/**
 * @fileoverview Sonner - Beautiful toast notifications
 *
 * Purpose: Display toast notifications using Sonner library
 * Dependencies: sonner, lucide-react
 * Used by: Success messages, errors, notifications throughout the app
 *
 * Note: Sonner is a simpler alternative to Toast component
 *
 * Features:
 * - Beautiful animations
 * - Success, error, warning, info, loading states
 * - Custom icons
 * - Theme-aware
 * - Swipe to dismiss
 * - Promise-based API
 *
 * @example
 * import { toast } from "sonner";
 *
 * // Simple toast
 * toast("Event has been created");
 *
 * // With description
 * toast("Event created", {
 *   description: "Monday, January 3rd at 6:00pm",
 * });
 *
 * // Success/Error
 * toast.success("Saved successfully");
 * toast.error("Something went wrong");
 */

import {
  CircleCheck,
  Info,
  LoaderCircle,
  OctagonX,
  TriangleAlert,
} from "lucide-react";
// Note: Using next-themes for theme detection (same as our ThemeSwitcher)
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;
type SonnerTheme = NonNullable<ToasterProps["theme"]>;

const SONNER_THEMES: readonly SonnerTheme[] = ["light", "dark", "system"];

function isSonnerTheme(value: string): value is SonnerTheme {
  return (SONNER_THEMES as readonly string[]).includes(value);
}

const Toaster = ({ theme: themeProp, ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const resolved = themeProp ?? theme;

  return (
    <Sonner
      {...props}
      theme={isSonnerTheme(resolved) ? resolved : "system"}
      className="toaster group"
      icons={{
        success: <CircleCheck className="h-4 w-4" />,
        info: <Info className="h-4 w-4" />,
        warning: <TriangleAlert className="h-4 w-4" />,
        error: <OctagonX className="h-4 w-4" />,
        loading: <LoaderCircle className="h-4 w-4 animate-spin" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
    />
  );
};

export { Toaster };
